import { Component, type ReactNode } from "react";
import {
  CHAVE_TRAVA,
  ehErroDeVersaoNova,
  jaRecarregouPorVersao,
  recarregarUmaVez,
} from "../lib/versaoNova";

/**
 * Anti-TELA-BRANCA no redeploy, para as páginas PÚBLICAS (/p/, /medicao/,
 * /confirmar/, /selo/) — as únicas que não passam pelo Layout e portanto não
 * têm o `ErrorBoundary` por dentro. Nelas, este aqui é o boundary mais próximo
 * do `<Suspense>` e é ele que trata a aba velha.
 *
 * 🔴 O QUE ESTE ARQUIVO FAZIA ANTES (main d4f73766) — dois defeitos:
 *
 * 1. Ele achava que cobria o sistema inteiro. Não cobria: em rota autenticada o
 *    `ErrorBoundary` do Layout (Layout.tsx:998) está MAIS PERTO da página lazy,
 *    e o React entrega o erro ao mais próximo. Os 13 registros da semana de
 *    29/07 passaram todos por lá. O reconhecimento agora mora nos dois, vindo
 *    do mesmo `lib/versaoNova` — uma regra só, sem cópia pra divergir.
 *
 * 2. 🟥 LAÇO INFINITO LATENTE. O `componentDidMount` fazia
 *    `sessionStorage.removeItem(FLAG)` com o comentário "montou (chunks
 *    carregaram)". Mas este boundary monta ACIMA do `<Suspense>`: ele commita
 *    junto com o fallback, antes e independentemente de o filho lazy carregar.
 *    Então a trava era apagada em TODO boot — inclusive no boot logo depois da
 *    recarga automática. Falha persistente daria: recarrega → monta → limpa a
 *    trava → falha de novo → recarrega… sem fim. A trava só não foi exercida
 *    porque o defeito nº 1 impedia este boundary de ver o erro. Agora a trava
 *    tem prazo (`JANELA_ANTI_LACO_MS`) e ninguém a apaga no boot.
 *
 * Este boundary NÃO reporta pro `app_errors` de propósito: quem cai aqui é
 * visitante sem sessão (proposta/medição pública) e o endpoint recusa sem
 * token. Quem registra a classe é o `ErrorBoundary`, onde há usuário logado.
 *
 * Erro que NÃO é de versão é RE-LANÇADO — não mascara bug real.
 */
export class ChunkErrorBoundary extends Component<{ children: ReactNode }, { err: Error | null }> {
  state: { err: Error | null } = { err: null };

  static getDerivedStateFromError(err: unknown): { err: Error } {
    return { err: err instanceof Error ? err : new Error(String(err)) };
  }

  componentDidCatch(err: Error) {
    // Devolve false quando a trava está armada → o render mostra a saída manual.
    if (ehErroDeVersaoNova(err)) recarregarUmaVez();
  }

  render() {
    const { err } = this.state;
    if (!err) return this.props.children;
    if (!ehErroDeVersaoNova(err)) throw err; // não é versão → propaga

    const moldura = {
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      fontFamily: "system-ui,-apple-system,sans-serif",
      color: "#475569",
      padding: 24,
    } as const;

    // Já recarregou uma vez e caiu aqui de novo → saída manual, sem laço.
    if (jaRecarregouPorVersao()) {
      return (
        <div style={moldura}>
          <div style={{ textAlign: "center", maxWidth: 360 }}>
            <div style={{ fontSize: 40 }}>🔄</div>
            <p style={{ marginTop: 8, fontWeight: 700, color: "#0f172a" }}>Saiu uma versão nova</p>
            <p style={{ fontSize: 14, marginTop: 4 }}>
              Não consegui atualizar sozinho. Recarregue a página para continuar.
            </p>
            <button
              onClick={() => {
                try {
                  sessionStorage.removeItem(CHAVE_TRAVA);
                } catch {
                  /* sem storage, o reload abaixo ainda resolve */
                }
                window.location.reload();
              }}
              style={{
                marginTop: 16,
                padding: "8px 18px",
                borderRadius: 8,
                border: "none",
                background: "#0F766E",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Recarregar agora
            </button>
          </div>
        </div>
      );
    }

    // Primeira ocorrência: o componentDidCatch está disparando a recarga.
    return <div style={{ ...moldura, color: "#94a3b8" }}>Saiu uma versão nova — atualizando…</div>;
  }
}
